import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SHARED_API_CONFIG } from '../config/api.config';
import { FeedbackService } from './feedback.service';
import { ApiErrorResponse } from '../models/api-response.model';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private _http = inject(HttpClient);
  private _apiConfig = inject(SHARED_API_CONFIG);
  private _feedback = inject(FeedbackService);

  private _baseHeaders = new HttpHeaders({
    accept: 'application/json',
    'Content-Type': 'application/json',
  });

  // ---------- GET ----------
  get<T>(endpoint: string, params?: HttpParams): Observable<T> {
    return this._http.get<T>(this._buildUrl(endpoint), { params, headers: this._baseHeaders }).pipe(
      map((response: unknown) => this._handleBusinessResponse<T>(response)),
      catchError((error) => this._handleHttpError(error))
    );
  }

  // ---------- POST ----------
  post<T>(endpoint: string, body?: unknown): Observable<T> {
    let headers = this._baseHeaders;

    if (body instanceof FormData) {
      headers = headers.delete('Content-Type');
    }

    return this._http
      .post<T>(this._buildUrl(endpoint), body, {
        headers,
      })
      .pipe(
        map((response: unknown) => this._handleBusinessResponse<T>(response)),
        catchError((error) => this._handleHttpError(error))
      );
  }

  // ---------- PUT ----------
  put<T>(endpoint: string, body?: unknown): Observable<T> {
    let headers = this._baseHeaders;

    if (body instanceof FormData) {
      headers = headers.delete('Content-Type');
    }

    return this._http
      .put<T>(this._buildUrl(endpoint), body, {
        headers,
      })
      .pipe(
        map((response: unknown) => this._handleBusinessResponse<T>(response)),
        catchError((error) => this._handleHttpError(error))
      );
  }

  // ---------- DELETE ----------
  delete<T>(endpoint: string, body?: unknown): Observable<void> {
    let headers = this._baseHeaders;

    if (body instanceof FormData) {
      headers = headers.delete('Content-Type');
    }

    return this._http
      .delete<void>(this._buildUrl(endpoint), {
        body,
        headers,
      })
      .pipe(
        map((response: unknown) => this._handleBusinessResponse<void>(response)),
        catchError((error) => this._handleHttpError(error))
      );
  }

  // =========================
  // Helpers
  // =========================

  private _buildUrl(endpoint: string): string {
    return `${this._apiConfig.apiBaseUrl}${endpoint}`;
  }

  /**
   * Analiza la respuesta de negocio
   * Si existe `code`, es error controlado del backend
   */
  private _handleBusinessResponse<T>(response: unknown): T {
    if (this._isApiErrorResponse(response)) {
      this._feedback.showError({
        message: response.message,
        skipTranslation: true,
      });

      throw response;
    }

    return response as T;
  }

  private _isApiErrorResponse(response: unknown): response is ApiErrorResponse {
    return (
      typeof response === 'object' &&
      response !== null &&
      'code' in response &&
      'message' in response
    );
  }

  /**
   * Analiza errores HTTP (status codes)
   */
  private _handleHttpError(error: HttpErrorResponse) {
    let message = 'SHARED.FEEDBACK.GENERAL_ERROR';
    console.log(error);

    if (error.status === 0) {
      message = 'Error de conexión con el servidor';
    } else if (error.status >= 400) {
      message = error.error.message;
    } else if (error.error?.message) {
      message = error.error.message;
    }

    this._feedback.showError({
      title: 'Error',
      message,
      skipTranslation: true,
      presentation: 'modal',
    });

    return throwError(() => error);
  }
}
